import { Box, Button, Modal, Tabs, Tab, Typography, Paper, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeUser, Branch, Group } from "../../types";
import { useToast } from "../../context/toast-context";
import { UserService } from "../../services/user-service";
import { GroupService } from "../../services/group-service";
import { BranchService } from "../../services/branch-service";
import { AddEmployeeFirstLoginReq, EmployeeFirstLogin, GroupFirstLogin } from "../../services/dto/request/user-request-dto";
import { AddEmployeeForm } from "../form/add-employee-form";
import { EmployeesTab } from "./employees-tab";
import { EditEmployeeTab } from "./edit-employee-tab";
import * as XLSX from 'xlsx';

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "75vw",
  height: "75vh",
  maxWidth: "none",
  maxHeight: "none",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

const TABS = {
  VIEW: 0,
  ADD: 1,
  BULK_UPLOAD: 2,
  EDIT: 3,
}

interface ExcelRow {
  index: number;
  fullName: string;
  email: string;
  hasQR: boolean;
  groups: string[]; // Cambiar de 'group: string' a 'groups: string[]'
  isValid: boolean;
  errors: string[];
}

interface ProcessModalData {
  rows: ExcelRow[];
  totalRows: number;
  validRows: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <div className="p-8">{children}</div>}
    </div>
  );
}

interface BranchEmployeesProps {
  branchId: number;
  selectedBranch?: Branch;
  updateBranch?: (branchId: number, amountAdded: number) => void;
}

export function BranchEmployees({ branchId, selectedBranch, updateBranch }: BranchEmployeesProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useToast();
  const userService = useMemo(() => new UserService(), []);
  const groupService = useMemo(() => new GroupService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const [tabValue, setTabValue] = useState(TABS.VIEW);
  const [employeeToEdit, setEmployeeToEdit] = useState<EmployeeUser | null>(null);
  
  // Estados para paginación tradicional
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50); // 50 empleados por página
  
  // Estados para carga masiva
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [processModalData, setProcessModalData] = useState<ProcessModalData | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue !== TABS.EDIT) {
      setEmployeeToEdit(null);
    }
  };

  const fetchEmployees = useCallback(async (page: number = 0) => {
    setIsLoading(true);

    try {
      const offset = page * pageSize;
      
      await userService.getEmployees(
        { limit: pageSize, offset, branchid: branchId },
        (data) => {
          setEmployees(data.users);
          setTotalCount(data.totalCount);
          setTotalPages(Math.ceil(data.totalCount / pageSize));
          setCurrentPage(page);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsLoading(false);
    }
  }, [userService, branchId, showToast, pageSize]);

  // Funciones para navegación de páginas
  const goToPage = useCallback((page: number) => {
    if (page >= 0 && page < totalPages) {
      fetchEmployees(page);
    }
  }, [fetchEmployees, totalPages]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1);
    }
  }, [currentPage, totalPages, goToPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 0) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, goToPage]);

  const fetchGroups = useCallback(async () => {
    try {
      await groupService.getGroups(
        branchId,
        (data) => {
          setGroups(data);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } catch (error) {
      showToast("Error al cargar los grupos", "error");
    }
  }, [groupService, branchId, showToast]);

  const downloadTemplate = useCallback(async () => {
    try {
      // Asegurar que los grupos estén cargados antes de generar el template
      if (groups.length === 0) {
        await fetchGroups();
      }
      
      // Obtener los nombres de los grupos disponibles (excluyendo QR)
      const groupNames = groups.filter(group => group.name !== "QR").map(group => group.name);
      
      // Si no hay grupos, mostrar mensaje informativo
      if (groupNames.length === 0) {
        showToast("No hay grupos disponibles en esta sucursal. Puede crear grupos nuevos directamente en el Excel.", "info");
      }
      
      // Crear los datos para la hoja "Datos" (solo encabezados y filas de ejemplo)
      const datosSheetData = [
        ["Nombre y Apellido", "Correo", "Tiene QR", "Grupos"], // Cambiar "Grupo" por "Grupos"
        ...(groupNames.length > 0 ? [
          ["Juan Pérez", "juan@email.com", "SI", `${groupNames[0]}${groupNames.length > 1 ? ';' + groupNames[1] : ''}`], // Ejemplo con grupos existentes
          ["María García", "maria@email.com", "NO", groupNames[0]], // Ejemplo con un grupo existente
        ] : [
          ["Juan Pérez", "juan@email.com", "SI", "BARRA;DELIVERY"], // Ejemplo con grupos nuevos
          ["María García", "maria@email.com", "NO", "COCINA"], // Ejemplo con grupo nuevo
        ]),
        ["", "", "", ""], // Fila vacía para ejemplo
      ];

      // Crear los datos para la hoja de "Grupos"
      const groupsSheetData = [
        ["Grupos Disponibles"], // Encabezado de la columna A
        ...groupNames.map(groupName => [groupName]), // Cada grupo en una fila de la columna A
        ...(groupNames.length === 0 ? [
          [""], // Fila vacía
          ["No hay grupos disponibles en esta sucursal"],
          [""],
          ["Puede crear grupos nuevos escribiendo nombres en la columna 'Grupos'"],
          ["Ejemplos: BARRA, DELIVERY, LIMPIEZA, COCINA, MESAS"]
        ] : [])
      ];

      // Crear los datos para la hoja de "Instrucciones"
      const instructionsSheetData = [
        ["INSTRUCCIONES PARA USAR EL TEMPLATE"],
        [],
        ["PASO 1: COMPLETAR LOS DATOS"],
        ["- Vaya a la hoja 'Datos'"],
        ["- Complete las filas con la información de los empleados"],
        ["- Puede agregar más filas según sea necesario"],
        [],
        ["PASO 2: FORMATO DE DATOS"],
        ["- Nombre y Apellido: Texto completo del empleado"],
        ["- Correo: Email válido del empleado"],
        ["- Tiene QR: Use EXACTAMENTE 'SI' o 'NO'"],
        ["- Grupos: Use uno o más grupos separados por punto y coma (;). Ejemplo: 'COCINA;MESAS' o solo 'COCINA'"],
        ["- NOTA: Si 'Tiene QR' es 'SI', automáticamente se agregará el grupo 'QR' aunque no se especifique en esta columna"],
        ["- IMPORTANTE: Si 'Tiene QR' es 'NO', DEBE especificar al menos un grupo"],
        [],
        ["PASO 3: GRUPOS DISPONIBLES Y NUEVOS"],
        ["- Consulte la hoja 'Grupos' para ver todos los grupos disponibles"],
        ["- NUEVA FUNCIONALIDAD: Puede crear grupos nuevos escribiendo nombres que no existan"],
        ["- Los grupos nuevos se crearán automáticamente durante la carga masiva"],
        ["- Los nombres de grupos deben tener al menos 2 caracteres"],
        ["- Ejemplo de grupos nuevos: 'BARRA', 'DELIVERY', 'LIMPIEZA'"],
        ["- IMPORTANTE: NO use 'QR' como nombre de grupo - se maneja automáticamente con 'TIENE QR'"],
        [],
        ["PASO 4: VALORES VÁLIDOS PARA 'TIENE QR'"],
        ["- SI: El empleado tendrá acceso a códigos QR"],
        ["- NO: El empleado NO tendrá acceso a códigos QR"],
        [],
        ["PASO 5: SUBIR EL ARCHIVO"],
        ["- Guarde el archivo en formato Excel (.xlsx)"],
        ["- Suba el archivo usando el botón 'Cargar Archivo Excel'"],
        ["- El sistema procesará automáticamente la hoja 'Datos'"],
        [],
        ["IMPORTANTE:"],
        ["- NO modifique los nombres de las hojas"],
        ["- NO elimine las columnas de encabezado"],
        ["- Asegúrese de que la hoja 'Datos' esté presente"],
        ["- Verifique que todos los datos estén completos antes de subir"],
      ];

      // Crear el workbook
      const wb = XLSX.utils.book_new();
      
      // Crear y configurar la hoja "Datos"
      const datosWs = XLSX.utils.aoa_to_sheet(datosSheetData);
      datosWs['!cols'] = [
        { wch: 25 }, // Nombre y Apellido
        { wch: 30 }, // Correo
        { wch: 15 }, // Tiene QR
        { wch: 25 }  // Grupo
      ];

      // Crear y configurar la hoja "Grupos"
      const groupsWs = XLSX.utils.aoa_to_sheet(groupsSheetData);
      groupsWs['!cols'] = [
        { wch: 30 } // Ancho de la columna de grupos
      ];

      // Crear y configurar la hoja "Instrucciones"
      const instructionsWs = XLSX.utils.aoa_to_sheet(instructionsSheetData);
      instructionsWs['!cols'] = [
        { wch: 60 } // Ancho para las instrucciones
      ];

      // Agregar las hojas al workbook en orden específico
      XLSX.utils.book_append_sheet(wb, datosWs, "Datos");
      XLSX.utils.book_append_sheet(wb, groupsWs, "Grupos");
      XLSX.utils.book_append_sheet(wb, instructionsWs, "Instrucciones");

      // Generar y descargar el archivo
      const fileName = `template_empleados_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      showToast("Template descargado exitosamente", "success");
    } catch (error) {
      showToast("Error al generar el template", "error");
    }
  }, [groups, showToast, fetchGroups]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      showToast("Archivo cargado exitosamente", "success");
    }
  }, [showToast]);

  // Funciones para manejar drag and drop
  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Solo cambiar el estado si realmente salimos del área de drop
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    
    // Validar que sea un archivo Excel
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    const allowedExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      showToast("Solo se permiten archivos Excel (.xlsx, .xls)", "error");
      return;
    }

    setUploadedFile(file);
    showToast("Archivo cargado exitosamente", "success");
  }, [showToast]);

  const validateExcelData = useCallback((data: any[]): ProcessModalData => {
    const rows: ExcelRow[] = [];
    let validRowsCount = 0;

    data.forEach((row, index) => {
      if (index === 0) return; // Saltar encabezados

      // Verificar si la fila está en blanco (todos los campos vacíos o undefined)
      const isEmptyRow = !row[0] && !row[1] && !row[2] && !row[3];
      if (isEmptyRow) return; // Ignorar filas en blanco

      const excelRow: ExcelRow = {
        index: index,
        fullName: row[0] || "",
        email: row[1] || "",
        hasQR: false,
        groups: [], // Cambiar a array
        isValid: true,
        errors: []
      };

      // Validar nombre y apellido
      if (!excelRow.fullName.trim()) {
        excelRow.errors.push("Nombre y apellido es requerido");
        excelRow.isValid = false;
      }

      // Validar correo
      if (!excelRow.email.trim()) {
        excelRow.errors.push("Correo es requerido");
        excelRow.isValid = false;
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(excelRow.email)) {
          excelRow.errors.push("Formato de correo inválido");
          excelRow.isValid = false;
        }
      }

      // Validar Tiene QR
      const hasQRValue = row[2];
      const hasQRValueStr = String(hasQRValue).toUpperCase().trim();
      if (hasQRValueStr === "SI" || hasQRValue === "true" || hasQRValue === true) {
        excelRow.hasQR = true;
      } else if (hasQRValueStr === "NO" || hasQRValue === "false" || hasQRValue === false) {
        excelRow.hasQR = false;
      } else {
        excelRow.errors.push("'Tiene QR' debe ser 'SI' o 'NO'");
        excelRow.isValid = false;
      }

      // Validar grupos (opcional)
      const groupsValue = row[3] || "";
      let groupNames: string[] = [];
      
      if (groupsValue.trim()) {
        // Separar grupos por punto y coma
        const rawGroupNames = groupsValue.split(';').map((g: string) => g.trim()).filter((g: string) => g.length > 0) as string[];
        
        if (rawGroupNames.length > 0) {
          // Validar que los nombres de grupos no estén vacíos y tengan caracteres válidos
          const invalidGroupNames: string[] = [];
          const validGroupNames: string[] = [];
          
          rawGroupNames.forEach(groupName => {
            // Validar que el nombre del grupo no esté vacío y tenga al menos 2 caracteres
            if (groupName.length < 2) {
              invalidGroupNames.push(`"${groupName}" (muy corto)`);
            } else if (groupName.trim().length === 0) {
              invalidGroupNames.push(`"${groupName}" (nombre vacío)`);
            } else if (groupName.toUpperCase() === "QR") {
              invalidGroupNames.push(`"${groupName}" (el grupo QR se maneja automáticamente con la columna "TIENE QR")`);
            } else {
              validGroupNames.push(groupName);
            }
          });
          
          if (invalidGroupNames.length > 0) {
            excelRow.errors.push(`Nombres de grupos inválidos: ${invalidGroupNames.join(', ')}`);
            excelRow.isValid = false;
          } else {
            groupNames = validGroupNames;
          }
        }
      }
      
      // Si tiene QR = true, agregar automáticamente el grupo "QR" si no está ya incluido
      if (excelRow.hasQR && !groupNames.includes("QR")) {
        groupNames.push("QR");
      }
      
      // Si tiene QR = false, debe tener al menos un grupo
      if (!excelRow.hasQR && groupNames.length === 0) {
        excelRow.errors.push("Al menos un grupo es requerido cuando 'Tiene QR' es 'NO'");
        excelRow.isValid = false;
      }
      
      excelRow.groups = groupNames;

      if (excelRow.isValid) {
        validRowsCount++;
      }

      rows.push(excelRow);
    });

    return {
      rows,
      totalRows: rows.length,
      validRows: validRowsCount
    };
  }, [groups]);

  const processExcelFile = useCallback(async () => {
    if (!uploadedFile) {
      showToast("Por favor selecciona un archivo", "error");
      return;
    }

    setIsProcessing(true);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Validar que exista la hoja "Datos"
          if (!workbook.SheetNames.includes("Datos")) {
            showToast("Archivo inválido. El Excel debe contener una hoja llamada 'Datos'", "error");
            setIsProcessing(false);
            return;
          }

          // Leer específicamente la hoja "Datos"
          const worksheet = workbook.Sheets["Datos"];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          // Validar que tenga las columnas requeridas
          const headers = jsonData[0] as string[];
          const requiredColumns = ["Nombre y Apellido", "Correo", "Tiene QR", "Grupos"];
          
          const missingColumns = requiredColumns.filter(col => !headers.includes(col));
          if (missingColumns.length > 0) {
            showToast(`Archivo no válido. Faltan columnas en la hoja 'Datos': ${missingColumns.join(', ')}`, "error");
            setIsProcessing(false);
            return;
          }

          const validatedData = validateExcelData(jsonData as any[]);
          setProcessModalData(validatedData);
          setSelectedRows(validatedData.rows.filter(row => row.isValid).map(row => row.index));
          setIsProcessModalOpen(true);

        } catch (error) {
          showToast("Error al procesar el archivo Excel", "error");
        } finally {
          setIsProcessing(false);
        }
      };

      reader.readAsArrayBuffer(uploadedFile);
    } catch (error) {
      showToast("Error al leer el archivo", "error");
      setIsProcessing(false);
    }
  }, [uploadedFile, showToast, validateExcelData]);

  const handleRowSelection = useCallback((rowIndex: number) => {
    setSelectedRows(prev => {
      if (prev.includes(rowIndex)) {
        return prev.filter(index => index !== rowIndex);
      } else {
        return [...prev, rowIndex];
      }
    });
  }, []);

  const handleSelectAllValid = useCallback(() => {
    if (!processModalData) return;
    
    const validRowIndexes = processModalData.rows
      .filter(row => row.isValid)
      .map(row => row.index);
    
    setSelectedRows(validRowIndexes);
  }, [processModalData]);

  const handleDeselectAll = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const clearUploadedFile = useCallback((showMessage = true) => {
    setUploadedFile(null);
    setProcessModalData(null);
    setSelectedRows([]);
    setIsProcessModalOpen(false);
    
    // Resetear el input de archivo
    const fileInput = document.getElementById('excel-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    
    if (showMessage) {
      showToast(t("branches.employees-info.file-cleared"), "success");
    }
  }, [showToast, t]);

  const processSelectedRows = useCallback(async () => {
    if (!processModalData || selectedRows.length === 0) {
      showToast("No hay filas seleccionadas para procesar", "error");
      return;
    }

    setIsProcessing(true);

    try {
      // Filtrar solo las filas seleccionadas y válidas
      const rowsToProcess = processModalData.rows.filter(row => 
        selectedRows.includes(row.index) && row.isValid
      );

      if (rowsToProcess.length === 0) {
        showToast("No hay filas válidas seleccionadas", "error");
        setIsProcessing(false);
        return;
      }

      // Encontrar la sucursal seleccionada para obtener equalSplitTip
      const currentBranch = selectedBranch;
      const equalSplitTip = currentBranch?.equalSplitTip || false;

      // Identificar grupos únicos de todas las filas a procesar
      const allGroupsFromExcel = new Set<string>();
      rowsToProcess.forEach(row => {
        row.groups.forEach(groupName => {
          if (groupName !== "QR") { // Excluir QR ya que se maneja por separado
            allGroupsFromExcel.add(groupName);
          }
        });
      });

      // Identificar grupos nuevos (que no existen en la sucursal)
      const existingGroupNames = groups
        .filter(group => group.name !== "QR")
        .map(group => group.name);
      
      const newGroups = Array.from(allGroupsFromExcel).filter(groupName => 
        !existingGroupNames.includes(groupName)
      );

      // Preparar los grupos existentes
      const existingGroupsForRequest: GroupFirstLogin[] = groups
        .filter(group => group.name !== "QR")
        .map(group => ({
          name: group.name,
          groupId: group.groupId
        }));

      // Preparar los grupos nuevos (con groupId = -1 para indicar que son nuevos)
      const newGroupsForRequest: GroupFirstLogin[] = newGroups.map(groupName => ({
        name: groupName,
        groupId: -1 // -1 indica que es un grupo nuevo que debe ser creado
      }));

      // Combinar grupos existentes y nuevos
      const allGroupsForRequest = [...existingGroupsForRequest, ...newGroupsForRequest];

      // Formatear empleados según la lógica del formulario de agregar empleado
      const formattedEmployees: EmployeeFirstLogin[] = rowsToProcess.map(row => ({
        name: row.fullName,
        email: row.email,
        useQR: equalSplitTip ? row.hasQR : row.hasQR,
        groupTypesQR: equalSplitTip ? false : row.hasQR,
        groupsNames: row.groups, // Usar el array de grupos directamente
      }));

      // Preparar el request según la estructura esperada por el backend
      const requestParams: AddEmployeeFirstLoginReq = {
        branchId: branchId,
        equalSplitTip: equalSplitTip,
        groups: equalSplitTip ? [] : allGroupsForRequest,
        employees: formattedEmployees,
      };

    // Cambiar la llamada en processSelectedRows:
    await userService.addEmployeesBulk(
        requestParams,
        async (data) => {
        const { summary, errors, hasErrors } = data;
        
        // Verificar si hay errores
        if (hasErrors && errors && errors.length > 0) {

          let message = "";
          
          // Si hay errores, mostrar información detallada y NO limpiar el archivo
          message = `Procesamiento completado con ${errors.length} errores: `;
          
          // Agregar detalles de errores
          message += `Errores: `;
          const errorDetails = errors.map((err: { email: string; error: string }) => 
            `${err.email} (${err.error})`
          ).join(', ');
          message += errorDetails;

          // Agregar información de éxito si la hay
          if (summary.new > 0 || summary.updated > 0) {
            const totalProcessed = summary.new + summary.updated;
            message += `. Procesados correctamente ${totalProcessed}: `;
            if (summary.new > 0) {
              message += summary.new === 1 
                ? `${summary.new} ${t("branches.employees-info.new-singular")}, ` 
                : `${summary.new} ${t("branches.employees-info.new-plural")}, `;
            }
            if (summary.updated > 0) {
              message += summary.updated === 1 
                ? `${summary.updated} ${t("branches.employees-info.updated-singular")}` 
                : `${summary.updated} ${t("branches.employees-info.updated-plural")}`;
            }
          }
          
          // Mostrar el toast con errores (no se cierra automáticamente)
          showToast(message, "warning", 0);
          
          // NO limpiar el archivo para que el usuario pueda corregir los errores
          // Solo cerrar el modal de procesamiento
          setIsProcessModalOpen(false);
          
        } else {
          // Mostrar mensaje de éxito completo
          let message = `${summary.total} ${t("branches.employees-info.processed")}: `;
          if (summary.new > 0) {
            message += summary.new === 1 
              ? `${summary.new} ${t("branches.employees-info.new-singular")}, ` 
              : `${summary.new} ${t("branches.employees-info.new-plural")}, `;
          }
          if (summary.updated > 0) {
            message += summary.updated === 1 
              ? `${summary.updated} ${t("branches.employees-info.updated-singular")}` 
              : `${summary.updated} ${t("branches.employees-info.updated-plural")}`;
          }
          
          showToast(message, "success");
          
          // Solo limpiar el archivo cuando no hay errores (sin mostrar mensaje)
          clearUploadedFile(false);
        }
          
          // Actualizar la lista de empleados - resetear a página 0 y forzar recarga
          setCurrentPage(0);
          setTotalPages(0);
          setTotalCount(0);
          fetchEmployees(0);
          
          // Actualizar el contador de la sucursal si está disponible
          if (updateBranch && selectedBranch) {
          updateBranch(selectedBranch.branchId, summary.new);
          }
          
          setIsProcessing(false);
        },
        (error) => {
          showToast(`Error al procesar empleados: ${error}`, "error");
          setIsProcessing(false);
        }
      );

    } catch (error) {
      showToast("Error inesperado al procesar los empleados", "error");
      setIsProcessing(false);
    }
  }, [processModalData, selectedRows, showToast, userService, branchId, selectedBranch, groups, fetchEmployees, updateBranch, clearUploadedFile]);

  useEffect(() => {
    const controller = new AbortController();

    if (isModalOpen) {
      // Resetear paginación cuando se abre el modal
      setCurrentPage(0);
      setTotalPages(0);
      setTotalCount(0);
      fetchEmployees(0);
      fetchGroups();
    }

    return () => {
      controller.abort();
    };
  }, [isModalOpen, fetchEmployees, fetchGroups]);

  const handleEditEmployee = (employee: EmployeeUser) => {
    setEmployeeToEdit(employee);
    setTabValue(TABS.EDIT);
  };

  const handleDeleteEmployee = (employeeId: number) => {
    setEmployees(prevEmployees => prevEmployees.filter(emp => emp.userId !== employeeId));
    if (updateBranch && selectedBranch) {
      updateBranch(selectedBranch.branchId, -1);
    }
    // Recargar la página actual después de eliminar
    fetchEmployees(currentPage);
  };

  const handleUpdateEmployees = async (updatedEmployees: EmployeeUser[]) => {
    // Recargar la página actual después de actualizar para obtener los datos más recientes
    await fetchEmployees(currentPage);
  };

  const handleOnLeaveChange = (userId: number, branchId: number, onLeave: boolean) => {
    setEmployees((current) =>
      current.map((employee) =>
        employee.userId === userId && employee.branchId === branchId
          ? { ...employee, onLeave }
          : employee
      )
    );
  };

  const handleApplyQrConfigToAll = (qrDisplayConfigId: number) => {
    branchService.setBranchEmployeesQrDisplayConfig(
      branchId,
      qrDisplayConfigId,
      () => {
        fetchEmployees(currentPage);
        showToast(t("employee.qr-config.success-all"), "success");
      },
      (error: string) => showToast(error, "error")
    );
  };

  return (
    <>
      <Button
        title={t("branches.employees-info.title")}
        onClick={() => setIsModalOpen(true)}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
          padding: "2px",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
            stroke="#EF0BB8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z"
            stroke="#EF0BB8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
            stroke="#EF0BB8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
            stroke="#EF0BB8"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Button>
      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setTabValue(TABS.VIEW);
            setEmployeeToEdit(null);
          }}
          aria-labelledby={t("branches.employees-info.title")}
          aria-describedby={t("branches.employees-info.title")}
        >
          <Box sx={style} className="custom-scrollbar">
            <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2 border-b border-gray-200">
              <h1 className="font-montserrat font-medium">
                {t("branches.employees-info.title")}
              </h1>
            </div>

            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTabs-indicator": {
                  backgroundColor: "#EF0BB8",
                },
                "& .MuiTab-root": {
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "#EF0BB8",
                  },
                },
              }}
            >
              <Tab label={t("branches.employees-info.title")} />
              <Tab label={t("branches.add-employee")} />
              <Tab label={t("branches.bulk-upload")} />
              {employeeToEdit && (
                <Tab label={t("branches.edit-employee")} />
              )}
            </Tabs>

            <TabPanel value={tabValue} index={TABS.VIEW}>
              <EmployeesTab 
                employees={employees}
                isLoading={isLoading}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onEditEmployee={handleEditEmployee}
                onDeleteEmployee={handleDeleteEmployee}
                onUpdateEmployees={handleUpdateEmployees}
                onOnLeaveChange={handleOnLeaveChange}
                onApplyQrConfigToAll={handleApplyQrConfigToAll}
                onGoToPage={goToPage}
                onGoToNextPage={goToNextPage}
                onGoToPreviousPage={goToPreviousPage}
                branchId={branchId}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={TABS.ADD}>
              <AddEmployeeForm 
                selectedBranch={selectedBranch}
                updateBranch={updateBranch}
                addEmployees={(updatedEmployees) => {
                  // Refrescar la lista completa desde el servidor para mostrar los nuevos empleados
                  setCurrentPage(0);
                  setTotalPages(0);
                  setTotalCount(0);
                  fetchEmployees(0);
                  setTabValue(TABS.VIEW);
                }}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={TABS.BULK_UPLOAD}>
              <div className="grid grid-cols-2 gap-8 h-full">
                {/* Primera mitad - Descarga de template */}
                <div className="text-center py-8 border-r border-gray-200">
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                      Descargar Template
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Descarga el template para cargar empleados de forma masiva
                    </p>
                  </div>
                  
                  <Button
                    variant="contained"
                    onClick={downloadTemplate}
                    sx={{
                      backgroundColor: "#EF0BB8",
                      color: "white",
                      fontFamily: "Montserrat",
                      fontWeight: 500,
                      textTransform: "none",
                      paddingX: 3,
                      paddingY: 1.5,
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "#d009a0",
                      },
                    }}
                  >
                    {t("branches.download-template")}
                  </Button>
                  
                  <div className="mt-6 text-sm text-gray-500">
                    <p>El template incluye 3 hojas:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Hoja "Datos":</strong> Para completar la información de empleados</li>
                      <li><strong>Hoja "Grupos":</strong> Lista de grupos disponibles para esta sucursal</li>
                      <li><strong>Hoja "Instrucciones":</strong> Guía detallada paso a paso</li>
                    </ul>
                    <p className="mt-3 font-medium text-gray-700">Estructura de la hoja "Datos":</p>
                    <ul className="list-disc list-inside mt-1 space-y-1">
                      <li>Columna "Nombre y Apellido"</li>
                      <li>Columna "Correo"</li>
                      <li>Columna "Tiene QR" (SI/NO)</li>
                      <li>Columna "Grupos" (requerido si "Tiene QR" es NO - separados por punto y coma. Si "Tiene QR" es SI, se agrega automáticamente "QR")</li>
                    </ul>
                    <p className="mt-3 font-medium text-gray-700">Funcionalidades:</p>
                    <ul className="list-disc list-inside mt-1 space-y-1 text-sm">
                      <li>Puede crear grupos nuevos escribiendo nombres que no existan</li>
                      <li>Los grupos nuevos se crearán automáticamente</li>
                      <li>Los nombres deben tener al menos 2 caracteres</li>
                    </ul>
                  </div>
                </div>

                {/* Segunda mitad - Carga de archivo */}
                <div className="py-8">
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-medium text-gray-800 mb-4">
                      Cargar Archivo Excel
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Sube tu archivo Excel completado para procesar los empleados
                    </p>
                  </div>

                  <Paper
                    elevation={2}
                    sx={{
                      border: uploadedFile ? "2px solid #EF0BB8" : isDragOver ? "2px solid #EF0BB8" : "2px dashed #ccc",
                      borderRadius: 2,
                      padding: 4,
                      textAlign: "center",
                      marginBottom: 3,
                      backgroundColor: uploadedFile ? "#f8f4ff" : isDragOver ? "#f8f4ff" : "#fafafa",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#EF0BB8",
                        backgroundColor: "#f8f4ff",
                      },
                    }}
                    onClick={() => document.getElementById('excel-file-input')?.click()}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <input
                      id="excel-file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    
                    {uploadedFile ? (
                      <div>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill="#EF0BB8"
                          className="mx-auto mb-2"
                        >
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
                        </svg>
                        <Typography variant="h6" sx={{ color: "#EF0BB8", fontWeight: "bold" }}>
                          {uploadedFile.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
                          Archivo cargado exitosamente
                        </Typography>
                      </div>
                    ) : (
                      <div>
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 24 24"
                          fill={isDragOver ? "#EF0BB8" : "#ccc"}
                          className="mx-auto mb-2"
                        >
                          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2M18 20H6V4H13V9H18V20Z"/>
                        </svg>
                        <Typography variant="h6" sx={{ color: isDragOver ? "#EF0BB8" : "#666" }}>
                          {isDragOver ? "¡Suelta el archivo aquí!" : "Arrastra y suelta tu archivo Excel aquí"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999", mt: 1 }}>
                          o haz clic para seleccionar un archivo
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#999", display: "block", mt: 2 }}>
                          Formatos soportados: .xlsx, .xls
                        </Typography>
                      </div>
                    )}
                  </Paper>

                  {uploadedFile && (
                    <div className="text-center space-y-3">
                      <div className="flex gap-3 justify-center">
                        <Button
                          variant="contained"
                          onClick={processExcelFile}
                          disabled={isProcessing}
                          sx={{
                            backgroundColor: "#EF0BB8",
                            color: "white",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            textTransform: "none",
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 2,
                            "&:hover": {
                              backgroundColor: "#d009a0",
                            },
                            "&:disabled": {
                              backgroundColor: "#ccc",
                            },
                          }}
                        >
                          {isProcessing ? "Procesando..." : "Procesar Archivo"}
                        </Button>
                        
                        <Button
                          variant="outlined"
                          onClick={() => clearUploadedFile()}
                          disabled={isProcessing}
                          sx={{
                            borderColor: "#666",
                            color: "#666",
                            fontFamily: "Montserrat",
                            fontWeight: 500,
                            textTransform: "none",
                            paddingX: 4,
                            paddingY: 1.5,
                            borderRadius: 2,
                            "&:hover": {
                              borderColor: "#333",
                              backgroundColor: "#f5f5f5",
                              color: "#333",
                            },
                            "&:disabled": {
                              borderColor: "#ccc",
                              color: "#ccc",
                            },
                          }}
                        >
                          Limpiar
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="mt-6 text-sm text-gray-500">
                    <p className="font-medium">Requisitos del archivo:</p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li><strong>Debe contener una hoja llamada "Datos"</strong></li>
                      <li>La hoja "Datos" debe tener las 4 columnas: Nombre y Apellido, Correo, Tiene QR, Grupos</li>
                      <li>El archivo debe seguir el formato del template descargado</li>
                      <li>Los valores de "Tiene QR" deben ser exactamente "SI" o "NO"</li>
                      <li>Los grupos pueden ser existentes o nuevos (requerido si "Tiene QR" es NO)</li>
                      <li>Los grupos nuevos se crearán automáticamente si no existen</li>
                    </ul>
                  </div>
                </div>
              </div>
            </TabPanel>

            {employeeToEdit && (
              <TabPanel value={tabValue} index={TABS.EDIT}>
                <EditEmployeeTab 
                  employee={employeeToEdit}
                  branchId={branchId}
                  onEmployeeUpdated={() => {
                    // Refrescar la lista completa desde el servidor después de editar
                    setCurrentPage(0);
                    setTotalPages(0);
                    setTotalCount(0);
                    fetchEmployees(0);
                    setTabValue(TABS.VIEW);
                    setEmployeeToEdit(null);
                  }}
                  onClose={() => {
                    setTabValue(TABS.VIEW);
                    setEmployeeToEdit(null);
                  }}
                />
              </TabPanel>
            )}
          </Box>
        </Modal>
      )}

      {/* Modal de procesamiento */}
      <Dialog
        open={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: "#f5f5f5", 
          borderBottom: "1px solid #e0e0e0",
          fontFamily: "Montserrat",
          fontWeight: 600
        }}>
          <div>
            Procesar Archivo Excel
          </div>
          {processModalData && (
            <div style={{ color: "#666", marginTop: 8, fontSize: "0.875rem" }}>
              Total de filas: {processModalData.totalRows} | Filas válidas: {processModalData.validRows} | Filas seleccionadas: {selectedRows.length}
            </div>
          )}
        </DialogTitle>
        
        <DialogContent sx={{ padding: 0 }}>
          {processModalData && (
            <>
              <Box sx={{ padding: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSelectAllValid}
                    sx={{
                      borderColor: "#EF0BB8",
                      color: "#EF0BB8",
                      "&:hover": {
                        borderColor: "#d009a0",
                        backgroundColor: "#f8f4ff",
                      },
                    }}
                  >
                    Seleccionar todas las válidas
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleDeselectAll}
                    sx={{
                      borderColor: "#666",
                      color: "#666",
                      "&:hover": {
                        borderColor: "#333",
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    Deseleccionar todas
                  </Button>
                </div>
              </Box>

              {/* Encabezados de columnas */}
              <Box sx={{ padding: 2, borderBottom: "2px solid #e0e0e0", backgroundColor: "#f8f9fa" }}>
                <div className="grid grid-cols-4 gap-4">
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Nombre y Apellido
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Correo
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Tiene QR
                  </Typography>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#666" }}>
                    Grupos
                  </Typography>
                </div>
              </Box>

              <List sx={{ maxHeight: "400px", overflow: "auto" }}>
                {processModalData.rows.map((row, index) => (
                  <ListItem key={index} divider sx={{ backgroundColor: row.isValid ? "#fff" : "#fff5f5" }}>
                    <ListItemButton 
                      onClick={() => handleRowSelection(row.index)}
                      disabled={!row.isValid}
                      sx={{
                        padding: 1,
                        "&:hover": {
                          backgroundColor: row.isValid ? "#f8f4ff" : "#fff0f0",
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedRows.includes(row.index)}
                          tabIndex={-1}
                          disableRipple
                          disabled={!row.isValid}
                          sx={{
                            color: "#EF0BB8",
                            "&.Mui-checked": {
                              color: "#EF0BB8",
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <div className="grid grid-cols-4 gap-4">
                            <div>
                              <Typography variant="body2" fontWeight="bold">
                                {row.fullName || "Sin nombre"}
                              </Typography>
                            </div>
                            <div>
                              <Typography variant="body2">
                                {row.email || "Sin correo"}
                              </Typography>
                            </div>
                            <div>
                              <Typography variant="body2">
                                {row.hasQR ? "SI" : "NO"}
                              </Typography>
                            </div>
                            <div>
                              <Typography variant="body2">
                                {row.groups.join(', ') || "Sin grupos"} {/* Mostrar grupos separados por coma */}
                              </Typography>
                            </div>
                          </div>
                        }
                        secondary={
                          !row.isValid && row.errors.length > 0 ? (
                            <div className="mt-1">
                              {row.errors.map((error, errorIndex) => (
                                <Typography 
                                  key={errorIndex} 
                                  variant="caption" 
                                  sx={{ color: "#d32f2f", display: "block" }}
                                >
                                  • {error}
                                </Typography>
                              ))}
                            </div>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {processModalData.rows.length === 0 && (
                <Box sx={{ padding: 4, textAlign: "center" }}>
                  <Typography variant="body1" color="textSecondary">
                    No se encontraron filas para procesar
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ padding: 2, borderTop: "1px solid #e0e0e0", backgroundColor: "#f5f5f5" }}>
          <Button 
            onClick={() => setIsProcessModalOpen(false)}
            sx={{ 
              color: "#666",
              fontFamily: "Montserrat",
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={selectedRows.length === 0 || isProcessing}
            onClick={processSelectedRows}
            sx={{
              backgroundColor: "#EF0BB8",
              color: "white",
              fontFamily: "Montserrat",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#d009a0",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {isProcessing ? "Procesando..." : `Procesar Seleccionadas (${selectedRows.length})`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
